/* eslint-disable no-case-declarations */
import { PersonaPresence } from '@fluentui/react';
import a from 'indefinite';
import { AgentState, ChannelType, IHandledWorkItem, VirtualConversationType, WorkItemState } from '../../models/reports.internal';
import { capitalize } from '../../utils/stringUtils';
import { IconAndTextDetails, getChannelTypeDetails, getVirtualConversationDetails } from '../../utils/visualConfigUtils';

export const mediaIcons = {
  'Email': 'Mail',
  'Messaging': 'CannedChat',
  'Voice': 'Phone',
  'Webchat': 'OfficeChat',
};

interface WorkItemDetails extends IconAndTextDetails {
  displayAsActivity: boolean;
  textFormatter?(state: string): string;
}

const defaultWorkItemTextFormatter = (workItemText: string) => `Handling ${a(workItemText)} task`;
const workItemStateTextFormatter = (state: string) => `In ${state}`;

export function getWorkItemDetails(channelTypeOrState: ChannelType | WorkItemState, virtualConversationType: VirtualConversationType | null): WorkItemDetails {
  if (virtualConversationType)
    return { ...getVirtualConversationDetails(virtualConversationType), displayAsActivity: true };

  const channelDetails = getChannelTypeDetails(channelTypeOrState);
  if (channelDetails)
    return { ...channelDetails, displayAsActivity: true };

  // from WorkItemState
  switch (channelTypeOrState.toLowerCase()) {
    case 'active':
      return { text: 'Active', icon: 'ConnectContacts', displayAsActivity: false };
    case 'afterwork':
      return { text: 'After work', icon: 'Completed', displayAsActivity: true, textFormatter: workItemStateTextFormatter };
    case 'closed':
      return { text: 'Closed', icon: 'ChromeClose', displayAsActivity: false };
    case 'lookup':
      return { text: 'Lookup', icon: 'Search', displayAsActivity: true, textFormatter: workItemStateTextFormatter };
    case 'preview':
      return { text: 'Preview', icon: 'CampaignTemplate', displayAsActivity: false };

    default:
      return { text: capitalize(channelTypeOrState), icon: 'Unknown', displayAsActivity: false };
  }
}

export interface ActivityDetails {
  presence: PersonaPresence;
  text: string;
  icon?: string;
  textStyle?: 'bold' | 'italic' | 'grey';
}

export function getActivityDetails(state: AgentState, breakName: string | null, currentlyHandledWorkItems: IHandledWorkItem[]): ActivityDetails {
  let activity = '';
  let icon = undefined;

  switch (state) {
    case 'Idle':
      return {
        presence: PersonaPresence.online,
        text: 'Currently available',
        textStyle: 'bold',
      };
    case 'Break':
      return {
        presence: PersonaPresence.away,
        text: breakName ? `On ${breakName}${breakName.toLowerCase().includes('break') ? '' : ' break'}` : 'On break',
      };
    case 'Busy':
      if (currentlyHandledWorkItems.length > 1) {
        activity = 'Handling multiple tasks';
        icon = 'Boards';
      }
      else if (currentlyHandledWorkItems.length === 1) {
        const workItemDetails = getWorkItemDetails(currentlyHandledWorkItems[0].channelType, currentlyHandledWorkItems[0].virtualConversationType);
        
        if (workItemDetails.displayAsActivity) {
          activity = (workItemDetails.textFormatter ?? defaultWorkItemTextFormatter)(workItemDetails.text);
          icon = workItemDetails.icon;
        }
        else {
          activity = 'Currently busy';
        }
      }
      else {
        activity = 'Currently busy';
      }
      
      return {
        presence: PersonaPresence.busy,
        text: activity,
        icon,
      };
    case 'LoggedOut':
      return {
        presence: PersonaPresence.offline,
        text: 'Currently logged out',
        textStyle: 'grey',
      };
  }
}

export function getIsMonitorable(channelType: ChannelType): boolean {
  switch (channelType.toLowerCase()) {
    case 'directline':
    case 'facebook':
    case 'instagram':
    case 'sms':
    case 'viber':
    case 'voice':
    case 'webchat':
    case 'whatsapp':
      return true;
    default:
      return false;
  }
}
